import { useCallback, useEffect, useState } from "react";

import { DateRange } from "react-day-picker";

import { format } from "date-fns";

import { DatePickerValue } from "./DatePicker.types";
import {  PickerStrategy } from "./DatePickerStrategy";
import { getDateRange } from "./utils";

export interface UseRangeDatePickerParams {
  value?: DatePickerValue;
  onChange?: (value?: DateRange) => void;
  onApply?: (value: DateRange) => void;
  onClear?: () => void;
  fixedRangeDays?: number;
  shouldExcludeFuture?: boolean;
  shouldExcludePast?: boolean;
  disableDatesAfter?: Date;
}

export const useRangeDatePicker = ({
  value,
  onChange,
  onApply,
  onClear,
  fixedRangeDays,
  shouldExcludeFuture,
  shouldExcludePast,
  disableDatesAfter,
}: UseRangeDatePickerParams): PickerStrategy => {

  const [dateRange, setDateRange] = useState<DateRange | undefined>(value ? value as DateRange : undefined);

  const onValueChange = useCallback(() => {
  if (value === undefined) {
    setDateRange(undefined);
    return;
  }
    setDateRange(value as DateRange);
  }, [value]);

  useEffect(() => {
    onValueChange();
  }, [value, onValueChange]);

  const getDisplayDate = useCallback(() => {
  if (!dateRange?.from) {
    return undefined;
  }
  
  if (dateRange.to) {
    return `${format(dateRange.from, 'PPP')} - ${format(dateRange.to, 'PPP')}`;
  }
  
  return format(dateRange.from, 'PPP');
  }, [dateRange]);



  const handleRangeSelect = (value: DateRange | undefined, selectedDay: Date) => {
    const inferredRange = getDateRange({
      value,
      selectedDay,
      fixedRangeDays,
      shouldExcludeFuture,
      shouldExcludePast,
      disableDatesAfter
    });

    setDateRange(inferredRange);
    onChange?.(inferredRange as DateRange);
  };

  const handleClear = () => {
    setDateRange(undefined);
    onChange?.(undefined);
    onClear?.();
  };

  const handleApply = () => {
    if(dateRange?.from && dateRange?.to) {
        onApply?.(dateRange);
    }
  };

  return {
    value: dateRange,
    getDisplayDate,
    onValueChange,
    select: (value, selectedDay) => handleRangeSelect(value as DateRange | undefined, selectedDay),
    apply: handleApply,
    clear: handleClear,
    isApplyDisabled: !dateRange?.from || !dateRange?.to,
  }
};